import React, { useMemo, useCallback } from 'react'
import { Stack } from '@mui/material'
import ScrollTable from 'components/global/CUK/ScrollTable'
import TableView, { useTable } from 'packages/ui/Table'
import useSort from '../../hooks/use-sort'
import COLUMNS_CONFIG_AUDIN, { ExpandedRowDetails } from './config-columns-audin'
import { useUser } from 'store'
import { useResponsiveColumns } from 'packages/ui/Table'
import { useSidebar } from 'components/AuthenticatedLayout/Sidebar/SidebarContext'

const RESPONSIVE_COLUMNS_CONFIG = {
  mobile: ['step', 'label', 'site_name', 'actions'],
  tablet: ['step', 'label', 'type', 'site_name', 'created_at', 'actions'],
  desktop: ['step', 'label', 'type', 'account', 'site_name', 'created_at', 'updated_at', 'format', 'actions'],
  desktopLarge: ['step', 'label', 'type', 'account', 'site_name', 'created_at', 'updated_at', 'format', 'actions']
}

const AudinTable = ({ documentSelected, apiDocuments, onRowClick }) => {
  const [user] = useUser();
  const { expanded: sidebarExpanded } = useSidebar();
  const apiSort = useSort({
    onChange: apiDocuments?.refetch,
  });

  const { visibleColumns, getVisibleColumns } = useResponsiveColumns(
    RESPONSIVE_COLUMNS_CONFIG,
    {
      sidebarExpanded,
      excludeColumns: documentSelected ? ['updated_at', 'account'] : []
    }
  );

  const columns = useMemo(() => {
    return COLUMNS_CONFIG_AUDIN.filter(({ name }) => visibleColumns.includes(name));
  }, [visibleColumns]);

  const allDocuments = useMemo(() => {
    return (apiDocuments?.documents || []).map(doc => ({
      ...doc,
      site_name: doc.site_name || (doc.sites && doc.sites[0]?.name),
    }));
  }, [apiDocuments?.documents]);

  const sortedDocuments = useMemo(() => {
    return apiSort.sort(allDocuments);
  }, [allDocuments, apiSort]);

  const handleRowClick = useCallback((doc) => {
    if (user.roles && user.roles.wifirst_documents) {
      onRowClick(doc);
    }
  }, [user.roles, onRowClick]);

  const responsiveConfig = useMemo(() => ({
    expandedRowRenderer: {
      component: ExpandedRowDetails,
      shouldRender: (screenSize) => screenSize === 'mobile',
      getVisibleColumns
    }
  }), [getVisibleColumns]);

  const apiTable = useTable({
    rowKey: 'id',
    loading: apiDocuments?.loading,
    items: sortedDocuments,
    columns,
    onRowClick: handleRowClick,
    responsiveConfig
  });

  return (
    <Stack
      sx={{
        overflow: 'hidden',
        overflowY: 'auto',
        flex: '1 1 auto',
        scrollbarWidth: 'none',
        height: '100%',
        '&::-webkit-scrollbar ': {
          display: 'none',
        },
      }}
    >
      <ScrollTable
        fetchNextPage={apiDocuments?.fetchNextPage}
        shouldFetchNextPage={apiDocuments?.shouldFetchNextPage}
        refetch={apiDocuments?.refetch}
        collapse={Boolean(documentSelected)}
      >
        <TableView
          apiTable={apiTable}
          sort={{
            by: apiSort.orderBy,
            direction: apiSort.orderDirection,
            onChange: (name) => {
              const column = columns.find((column) => column.name === name);
              apiSort.orderByChange(column.sortBy || column.name);
            },
          }}
        />
      </ScrollTable>
    </Stack>
  );
};

export default AudinTable;